import styled from 'styled-components';
import { getIsTaskeen, getPhoneNumber } from '../../helpers/common';
import { useTranslation } from 'next-i18next';
import { useSelector } from 'react-redux';
import { phoneHelper } from '../../helpers/phone-helper';
import ButtonTextIcon from './ButtonTextIcon';
import CallIcon from '../svg-icons/CallIcon';
import WhatsappIcon from '../svg-icons/WhatsappIcon';
import {
  CTATypes,
  EntityTypes,
  SearchViewTypes
} from '../../new-lib/constants/common';
import { logEvent } from '../../new-lib/analytics';
import { MouseEvent } from 'react';
import { trackEvent } from '../../new-lib/remarketing';
import { useRouter } from 'next/router';
import WhatsappBuilder from '../../helpers/whatsapp/whatsapp-builder';
import { WhatsappInstance } from '../../new-lib/types/whatsapp';
import useTrackExperiment from '../../hooks/TrackExperiment';

interface CTAProps {
  whatsapp?: WhatsappInstance;
  entity: any;
  entityType: any;
  fillContainer?: boolean;
  phoneNumber?: string;
  eventExtraParams?: {};
}
const CTA = ({
  whatsapp,
  entity,
  entityType,
  fillContainer = false,
  phoneNumber,
  eventExtraParams
}: CTAProps) => {
  const { t } = useTranslation('common');

  const experimentVariant = useSelector(
    (state: any) => state.app.experimentVariant
  );

  const referer = useSelector((state: any) => state.app.referer);
  const userId = useSelector((state: any) => state.app.userId);
  const eventId = useSelector((state: any) => state.app.eventId);
  const isMobile = useSelector((state: any) => state.app.isMobile);
  const sahelModal = useSelector((state: any) => state.app.sahelModal);

  const { asPath, locale, query } = useRouter();

  useTrackExperiment('cta_text');

  const getWhatsappInstance = () => {
    if (!whatsapp) {
      const whatsapp = new WhatsappBuilder(
        entity,
        asPath,
        referer,
        userId,
        eventId,
        entityType as CTATypes,
        locale,
        undefined
      );

      return whatsapp;
    }

    return whatsapp;
  };

  const handleContactEvent = (contactType: 'call' | 'whatsapp') => {
    const randomEventId = localStorage.getItem('random_event_id');

    logEvent.contact(
      contactType,
      `${entityType}Info`,
      eventId ?? randomEventId,
      eventExtraParams
    );
    trackEvent.purchase({
      listingIds: [entity.id],
      listingPrice: entity.prices?.minPrice || entity.min_price
    });
  };

  const PHONE_NUMBER =
    phoneNumber ?? getPhoneNumber(referer, getIsTaskeen(entity));

  const handleWhatsAppClick = (e: MouseEvent) => {
    e.preventDefault();
    getWhatsappInstance().launchWhatsApp();
    handleContactEvent('whatsapp');
  };

  const handlePhoneCall = (e: MouseEvent) => {
    phoneHelper(entity, asPath, Number(PHONE_NUMBER), entityType as CTATypes);
    handleContactEvent('call');
  };

  const showExperimentText =
    isMobile &&
    experimentVariant != 'cta_empty' &&
    query?.view !== SearchViewTypes.MAP &&
    !asPath.includes('/nawy-now') &&
    !sahelModal;

  return (
    <Container id="cta" fillContainer={fillContainer}>
      {showExperimentText ? (
        <div className="cta-text">
          <div className="separator"></div>
          <span className="text">{t(experimentVariant)}</span>
        </div>
      ) : (
        <></>
      )}
      <div
        className="btns-container"
        itemScope
        itemType="https://schema.org/Action"
      >
        <a href={`tel:+2${PHONE_NUMBER}`} itemProp="url">
          <ButtonTextIcon
            backgroundColor="#E4EBF2"
            backgroundHover="#D4DBE2"
            color="#1E4164"
            fontSize="14px"
            padding="8px 16px"
            border="none"
            text={t('call_us')}
            icon={<CallIcon />}
            onClick={e => handlePhoneCall(e)}
          />
        </a>

        {entityType != EntityTypes.AREA &&
          entityType != EntityTypes.DEVELOPER && (
            <ButtonTextIcon
              backgroundColor="#4CD964"
              backgroundHover=" #40B855"
              color="white"
              fontSize="14px"
              icon={<WhatsappIcon />}
              padding="8px 16px"
              text={t('whatsapp')}
              border="none"
              onClick={e => handleWhatsAppClick(e)}
            />
          )}
      </div>
    </Container>
  );
};

const Container = styled.div<any>`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  .cta-text {
    display: flex;
    flex-direction: row;
    align-items: center;
    text-transform: capitalize;
    gap: 10px;
    .separator {
      width: 6px;
      height: 100%;
      background-color: ${props => props.theme.light.colors.primaryOrange};
    }
    .text {
      font-family: materialBold;
      font-size: ${props => props.theme.font.sizes.xxl};
      color: #0c314e;
    }
  }
  .btns-container {
    display: flex;
    flex-direction: row;
    justify-content: center;
    gap: 8px;
    width: 100%;

    > * {
      ${props => (props?.fillContainer ? 'width: 50%' : 'width: initial')};
    }

    button {
      font-size: ${props => props.theme.font.sizes.l} !important;
      font-family: materialMedium !important;
      border-radius: 8px !important;
      height: 45px !important;
      width: 100%;
    }

    @media screen and (max-width: ${props =>
        props.theme.breakPoints.tablet.max}) {
      > div,
      > a {
        width: 50%;
      }
    }
  }
`;
export default CTA;
